import { useMemo } from 'react';
import { FrameworkListingLibProvider } from 'framework-listing-lib';
import PortalIdParser from 'PortalIdParser';
import { transformColumnPropertyOverrideForNewFilterEditor } from '../utils/filterPropertyUtils';
import SearchTokensProvider from './SearchTokensProvider';
import useCrmFetcher from '../hooks/useCrmFetcher';
import useTrackerProps from '../hooks/useTrackerProps';
import useContentPlatformPropertiesConfiguration from '../hooks/useContentPlatformPropertiesConfiguration';
import CONTENT_OBJECT_TYPE_DEFINITION from '../constants/ContentObjectTypeDefinition';
import { useAuthInfoProvider } from 'content-management-data-lib';
import { hasCrmAccessScopes } from '../utils/authUtils';
import useContentPlatformListingConfiguration from '../hooks/useContentPlatformListingConfiguration';
import { jsx as _jsx } from "react/jsx-runtime";
const SEARCH_INPUT_PROPS = {
  hasSearchInput: true,
  minSearchLength: 1,
  width: 300
};
const FrameworkListingLibProviderWithContentConfig = ({
  onViewIdChange,
  currentViewId,
  children,
  filterValidationConfig,
  hiddenFilter,
  emptyStateFilter,
  skipQuickfetchContentByViewOptimization,
  defaultViewId
}) => {
  const {
    hasViews,
    savedViewsNamespace,
    crmObjectTypeId
  } = useContentPlatformListingConfiguration();
  const {
    crmFetcher,
    searchTokens
  } = useCrmFetcher({
    filterValidationConfig,
    hiddenFilter,
    emptyStateFilter,
    currentViewId,
    skipQuickfetchContentByViewOptimization,
    defaultViewId,
    hasViewsAccess: hasViews
  });
  const {
    quickFilterPropertyNames
  } = useContentPlatformPropertiesConfiguration();
  const viewProps = useMemo(() => ({
    defaultQuickFetchViewId: '-2',
    hasViews,
    currentViewId,
    onViewIdChange,
    allViewsUrl: `/content-saved-views/${PortalIdParser.get()}/all`,
    namespace: savedViewsNamespace
  }), [hasViews, currentViewId, onViewIdChange, savedViewsNamespace]);
  const {
    propertyDefinitionMap
  } = useContentPlatformPropertiesConfiguration();
  const {
    isAvailableAsFilterOnly
  } = filterValidationConfig;
  const propertyDefinitionMapForColumns = Object.entries(propertyDefinitionMap).reduce((acc, [key, value]) => {
    if (!isAvailableAsFilterOnly(key)) {
      acc[key] = value;
    }
    return acc;
  }, {});
  const columnPropertyOverride = useMemo(() => {
    const transform = transformColumnPropertyOverrideForNewFilterEditor(propertyDefinitionMapForColumns);
    return {
      transform,
      propertyMap: Object.keys(propertyDefinitionMapForColumns).reduce((acc, key) => {
        acc[key] = transform(propertyDefinitionMapForColumns[key]);
        return acc;
      }, {})
    };
  }, [propertyDefinitionMapForColumns]);
  const quickFilterProps = useMemo(() => ({
    propertyNames: quickFilterPropertyNames || []
  }), [quickFilterPropertyNames]);
  const {
    tableTrackerProps
  } = useTrackerProps();
  const {
    userInfo
  } = useAuthInfoProvider();
  const {
    scopes
  } = userInfo.user;
  return /*#__PURE__*/_jsx(SearchTokensProvider, {
    searchTokens: searchTokens,
    children: /*#__PURE__*/_jsx(FrameworkListingLibProvider, {
      objectType: crmObjectTypeId,
      viewProps: viewProps,
      searchInputProps: SEARCH_INPUT_PROPS,
      trackerProps: tableTrackerProps,
      fetcher: crmFetcher,
      propertyOverride: columnPropertyOverride,
      quickFilterProps: quickFilterProps,
      hiddenFilter: hiddenFilter,
      emptyStateFilter: emptyStateFilter,
      typeDefinitionOverride: CONTENT_OBJECT_TYPE_DEFINITION,
      skipCrm: !hasCrmAccessScopes(scopes),
      children: children
    })
  });
};
export default FrameworkListingLibProviderWithContentConfig;